'use client';

import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';
import { ArrowDownIcon } from 'lucide-react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { cn } from '@/lib/utils';
import { Button } from '@deepguide-ai/ui/button';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { useTheme } from '@/context/theme-context'


export type ConversationProps = ComponentProps<typeof StickToBottom>

export const Conversation = ({
  className,
  children,
  ...props
}: ConversationProps) => (
  <StickToBottom
    className={cn('relative flex-1 overflow-hidden', className)}
    initial='smooth'
    resize='instant'
    role='log'
    {...props}
  >
    {(context) => (
      <ScrollArea
        ref={context.scrollRef}
        type='always'
        className='h-full w-full'
        viewClassName='h-full w-full [&>div]:!block'
      >
        {typeof children === 'function' ? children(context) : children}
      </ScrollArea>
    )}
  </StickToBottom>
)

export type ConversationContentProps = ComponentProps<'div'>

export const ConversationContent = ({
  className,
  children,
  ...props
}: ConversationContentProps) => {
  const { contentRef } = useStickToBottomContext()

  return (
    <div {...props} ref={contentRef} className={cn('p-4', className)}>
      {children}
    </div>
  )
}

export type ConversationEmptyStateProps = ComponentProps<'div'> & {
  title?: string
  description?: string
  icon?: React.ReactNode
}

export const ConversationEmptyState = ({
  className,
  title = 'No messages yet',
  description = 'Start a conversation to see messages here',
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => {
  const { theme } = useTheme()

  const memoizedIcon = useMemo(() => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='36'
      height='42'
      viewBox='0 0 36 42'
      fill='none'
    >
      <path
        d='M14.9714 0C26.5851 0 36 9.40205 36 21C36 32.5979 26.5851 42 14.9714 42H5.37152L9.44689 35.0229C9.89547 34.2548 10.7189 33.7825 11.6093 33.7825H14.9714C22.0407 33.7825 27.7714 28.0597 27.7714 21C27.7714 13.9403 22.0407 8.21747 14.9714 8.21747H11.6811C10.7464 8.21747 9.88185 7.72152 9.41073 6.91512L5.37152 0H14.9714ZM0 13.5707C2.01882 14.8307 6.64496 12.4787 11.8601 15.7547C16.15 18.4427 16.234 23.6507 16.15 25.4147C14.9725 23.7347 13.4583 22.3065 11.8601 21.0466C9.58908 19.2825 7.14979 17.6866 4.4581 16.6786C6.64513 18.3586 8.9162 19.9545 11.1031 21.7185C12.449 22.8105 13.7107 23.9026 14.9723 25.0786C15.3089 25.3306 15.5612 25.6667 15.8976 26.0867C2.11367 30.0316 0.00331899 13.5963 0 13.5707Z'
        fill={theme === 'dark' ? 'white' : '#1D4ED8'}
      />
    </svg>
  ), [theme])

   
  return(
  <div
    className={cn(
      'flex size-full flex-col items-center justify-center gap-6 py-2 text-center',
      className
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon ? (<div className='text-muted-foreground'>{icon}</div>) : memoizedIcon}

        <div className='space-y-2'>
          <h3 className='text-xl font-semibold leading-7'>{title}</h3>
          {description && (
            <p className='text-muted-foreground text-sm'>{description}</p>
          )}
        </div>
      </>
    )}
  </div>
)}

export type ConversationScrollButtonProps = ComponentProps<typeof Button>

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext()

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom()
  }, [scrollToBottom])

  return (
    !isAtBottom && (
      <Button
        className={cn(
          'absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full',
          className
        )}
        onClick={handleScrollToBottom}
        size='icon'
        type='button'
        variant='outline'
        {...props}
      >
        <ArrowDownIcon className='size-4' />
      </Button>
    )
  )
}
